import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileMenu } from './MobileMenuContext';

const Sidebar: React.FC = () => {
    const navigate = useNavigate();
    const { isMobileMenuOpen, closeMobileMenu } = useMobileMenu();

    // Le chemin actuel est utilisé pour la mise en surbrillance
    const currentPath = window.location.pathname;

    // Fonction utilitaire pour vérifier si le lien est actif
    const isActive = (path: string) => currentPath === path || currentPath.startsWith(`${path}/`);

    // Styles pour le lien actif
    const activeClass = 'bg-warning/20 text-warning font-extrabold border-warning/50';
    // Styles pour le lien inactif
    const inactiveClass = 'text-base-content hover:bg-base-300';

    // Handler de navigation qui ferme le menu mobile
    const handleNavigate = (path: string) => {
        navigate(path);
        closeMobileMenu();
    };

    return (
        <>
            {/* Sidebar Desktop */}
            <aside className="hidden lg:flex lg:w-64 xl:w-70 bg-base-100 shadow-2xl flex-col">
            {/* Logo Fidex Conseil */}
            <div className="p-3 xl:p-4 flex items-center justify-center border-b border-base-300">
                <img src="/fidexLogo.png" alt="Fidex Conseil Logo" className="h-16 xl:h-20 w-auto" />
            </div>

            {/* Navigation Sidebar */}
            <ul className="menu p-3 xl:p-4 w-full text-base-content text-base xl:text-lg font-medium space-y-1">
                <li className='rounded-box'>
                    <a
                        onClick={() => handleNavigate('/dashboard')}
                        className={isActive('/dashboard') ? activeClass : inactiveClass}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 00-1-1H9a1 1 0 00-1 1v4a1 1 0 001 1zm-6 0h14" /></svg>
                        Accueil
                    </a>
                </li>
                <li className='rounded-box'>
                    <a onClick={() => handleNavigate('/creation-employes')} className={isActive('/creation-employes') ? activeClass : inactiveClass}>
                        {/* Icône crayon pour création employés */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span className="hidden xl:inline">Création d'employés</span>
                        <span className="xl:hidden">Créer Employé</span>
                    </a>
                </li>
                <li className='rounded-box'>
                    <a onClick={() => handleNavigate('/employees')} className={isActive('/employees') ? activeClass : inactiveClass}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        Employés
                    </a>
                </li>
                <li className='rounded-box'>
                    <a onClick={() => handleNavigate('/bulletin-de-paie')} className={isActive('/bulletin-de-paie') ? activeClass : inactiveClass}>
                        {/* Icône argent pour bulletin de paie */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Bulletin de paie
                    </a>
                </li>
                <li className='rounded-box'>
                    <a onClick={() => handleNavigate('/documents')} className={isActive('/documents') ? activeClass : inactiveClass}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Documents
                    </a>
                </li>
                <li className='rounded-box'>
                    <a onClick={() => handleNavigate('/fidex-ia')} className={isActive('/fidex-ia') ? activeClass : inactiveClass}>
                        {/* Icône ampoule pour Fidex IA */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Fidex IA
                    </a>
                </li>

                {/* Séparateur avec style moderne */}
                <div className="divider my-2 text-xs font-semibold text-base-content/50">COMMUNICATION</div>

                {/* Fidex Email avec design moderne */}
                <li className='rounded-box'>
                    <a
                        onClick={() => handleNavigate('/fidex-email')}
                        className={`${isActive('/fidex-email') ? activeClass : inactiveClass} ${isActive('/fidex-email') ? '' : 'hover:bg-gradient-to-r hover:from-warning/10 hover:to-orange-500/10'} transition-all duration-200`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Fidex Email
                    </a>
                </li>
            </ul>
        </aside>

            {/* Menu Mobile - Drawer overlay */}
            {isMobileMenuOpen && (
                <>
                    {/* Overlay sombre */}
                    <div
                        className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                        onClick={closeMobileMenu}
                    />

                    {/* Sidebar mobile */}
                    <aside className="lg:hidden fixed left-0 top-0 h-full w-64 bg-base-100 shadow-2xl z-50 flex flex-col">
                        {/* Header avec bouton fermer */}
                        <div className="p-4 flex items-center justify-between border-b border-base-300">
                            <img src="/fidexLogo.png" alt="Fidex Conseil Logo" className="h-16 w-auto" />
                            <button
                                className="btn btn-ghost btn-circle btn-sm"
                                onClick={closeMobileMenu}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Navigation Sidebar Mobile */}
                        <ul className="menu p-4 w-full text-base-content text-base font-medium space-y-1 overflow-y-auto">
                            <li className='rounded-box'>
                                <a
                                    onClick={() => handleNavigate('/dashboard')}
                                    className={isActive('/dashboard') ? activeClass : inactiveClass}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 00-1-1H9a1 1 0 00-1 1v4a1 1 0 001 1zm-6 0h14" /></svg>
                                    Accueil
                                </a>
                            </li>
                            <li className='rounded-box'>
                                <a onClick={() => handleNavigate('/creation-employes')} className={isActive('/creation-employes') ? activeClass : inactiveClass}>
                                    {/* Icône crayon pour création employés (mobile) */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                    Création d'employés
                                </a>
                            </li>
                            <li className='rounded-box'>
                                <a onClick={() => handleNavigate('/employees')} className={isActive('/employees') ? activeClass : inactiveClass}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    Employés
                                </a>
                            </li>
                            <li className='rounded-box'>
                                <a onClick={() => handleNavigate('/bulletin-de-paie')} className={isActive('/bulletin-de-paie') ? activeClass : inactiveClass}>
                                    {/* Icône argent pour bulletin de paie (mobile) */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Bulletin de paie
                                </a>
                            </li>
                            <li className='rounded-box'>
                                <a onClick={() => handleNavigate('/documents')} className={isActive('/documents') ? activeClass : inactiveClass}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    Documents
                                </a>
                            </li>
                            <li className='rounded-box'>
                                <a onClick={() => handleNavigate('/fidex-ia')} className={isActive('/fidex-ia') ? activeClass : inactiveClass}>
                                    {/* Icône ampoule pour Fidex IA (mobile) */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    Fidex IA
                                </a>
                            </li>

                            {/* Séparateur mobile */}
                            <div className="divider my-2 text-xs font-semibold text-base-content/50">COMMUNICATION</div>

                            {/* Fidex Email mobile */}
                            <li className='rounded-box'>
                                <a
                                    onClick={() => handleNavigate('/fidex-email')}
                                    className={`${isActive('/fidex-email') ? activeClass : inactiveClass} ${isActive('/fidex-email') ? '' : 'hover:bg-gradient-to-r hover:from-warning/10 hover:to-orange-500/10'} transition-all duration-200`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Fidex Email
                                </a>
                            </li>
                        </ul>
                    </aside>
                </>
            )}
        </>
    );
};

export default Sidebar;